/**
 * ENG-2731 — where the desktop app's auth handoff is allowed to send tokens.
 *
 * ## The bug this closes
 *
 * `?desktopApp=true&callbackUrl=…` tells this app where to deliver the signed-in user's `idToken`
 * **and `refreshToken`**. The previous check accepted any `haven:` URL, or any `http:` URL on
 * `localhost`/`127.0.0.1`. The loopback branch was meant to be a development affordance — the call
 * site said so — but nothing enforced it, so production `account.starthaven.com` honoured it too:
 *
 *     https://account.starthaven.com/?desktopApp=true&callbackUrl=http://localhost:9999/x
 *
 * After sign-in the browser navigated there carrying both tokens in the query string. Exploiting it
 * needs something listening on the victim's loopback plus a click, so it is not trivial — but the
 * payload is a REFRESH token, which is durable account takeover with no re-authentication. And it
 * does not need malware to do damage: ordinary local dev servers log full request URLs, so a benign
 * listener writes the refresh token to disk.
 *
 * ## Why the gate is the app's own origin, not the build env
 *
 * `NODE_ENV` cannot express this. All three deploy targets — production, alpha and dev — run
 * `react-scripts build`, so every one of them is `production`. A build-env gate would either break
 * local development or leave alpha and dev wide open.
 *
 * The origin is the honest discriminator, because it is exactly the boundary that matters: an app
 * being served from loopback is a developer's own machine, and a loopback callback is then a
 * same-machine handoff. An app served from a real host handing tokens to loopback never makes sense.
 *
 * **Deliberate consequence:** the local HTTP callback no longer works against the *hosted* alpha and
 * dev sites, only against a locally-run account app. That is intended rather than overlooked —
 * alpha and dev issue real Firebase credentials for the same auth project as production, so a token
 * leaked from alpha is a production token.
 */

/** Loopback hosts a developer's own machine can answer on. */
const LOOPBACK_HOSTS = new Set(["localhost", "127.0.0.1", "[::1]", "::1"]);

function isLoopbackHost(hostname: string): boolean {
  return LOOPBACK_HOSTS.has(hostname.toLowerCase());
}

/**
 * Whether `url` may receive the desktop auth handoff, given the origin this app is served from.
 *
 * @param url         the `callbackUrl` query parameter — attacker-controllable
 * @param appHostname the hostname this app is running on (`window.location.hostname`)
 */
export function isSafeDesktopCallback(url: string, appHostname: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return false;
  }

  // The custom protocol is handled by the desktop app on this machine. There is no network hop and
  // no origin to confuse, so the scheme alone is the check.
  if (parsed.protocol === "haven:") return true;

  // The loopback affordance, available only to a locally-served app. Note both sides are required:
  // a local app may not target a remote host, and a remote app may not target loopback.
  if (parsed.protocol === "http:" && isLoopbackHost(parsed.hostname)) {
    return isLoopbackHost(appHostname);
  }

  return false;
}
